/**
 * Utility functions for comparison rule editor
 */

/**
 * Normalizes a field name for comparison by removing special characters,
 * trimming whitespace, and converting to lowercase
 */
export const normalizeFieldName = (fieldName: string): string => {
  return fieldName
    .trim()
    .toLowerCase()
    .replace(/[-_\s]/g, '');
};

/**
 * Finds the best matching field from available columns based on rule name
 * Priority: exact match > normalized match > none
 * 
 * @param ruleName - The name of the rule to match against
 * @param availableColumns - Array of available column names
 * @returns The matched column name or null if no match found
 */
export const findMatchingField = (
  ruleName: string,
  availableColumns: string[]
): string | null => {
  if (!ruleName || !availableColumns || availableColumns.length === 0) {
    return null;
  }

  const trimmedRuleName = ruleName.trim();
  
  // Priority 1: Exact match (case-insensitive)
  const exactMatch = availableColumns.find(
    (col) => col.toLowerCase() === trimmedRuleName.toLowerCase()
  );
  if (exactMatch) {
    return exactMatch;
  }

  // Priority 2: Normalized match (ignore underscores, hyphens, case)
  const normalizedRuleName = normalizeFieldName(trimmedRuleName);
  const normalizedMatch = availableColumns.find(
    (col) => normalizeFieldName(col) === normalizedRuleName
  );
  if (normalizedMatch) {
    return normalizedMatch;
  }

  // No match found
  return null;
};

/**
 * Determines default resources for left and right operands based on
 * configured comparison resources from the current comparison context.
 * Uses the first two configured resources (left = first, right = second).
 *
 * @param resourcesData - Array of configured comparison resources with rId
 * @returns Object with leftResourceId and rightResourceId, or null if defaults can't be determined
 */
export const getDefaultResources = (
  resourcesData: any[]
): { leftResourceId: number; rightResourceId: number } | null => {
  if (!resourcesData || resourcesData.length < 2) {
    return null;
  }

  // Use the first two configured resources from the comparison context
  // First resource = left operand, second resource = right operand
  return {
    leftResourceId: resourcesData[0].rId,
    rightResourceId: resourcesData[1].rId,
  };
};

/**
 * Message shown when a comparison rule is saved without both operand columns
 */
export const MISSING_REQUIRED_COLUMNS_MESSAGE =
  "Please select the left and right columns for all custom comparison rules";

/**
 * Checks whether an operand is missing its required column selection
 *
 * @param operand - The left_operand or right_operand of a comparison rule
 * @returns true when no column name has been selected
 */
export const isOperandColumnMissing = (operand: any): boolean => {
  return !operand?.column_name || operand.column_name.trim() === "";
};

/**
 * Collects the names of comparison rules that have a resource configured but
 * are missing a column selection on either operand. Rules without a resource
 * are skipped because their operands are not yet part of the payload.
 *
 * @param customComparisonRules - Array of custom comparison rules
 * @returns Names of the rules with a missing required column
 */
export const getRulesWithMissingColumns = (
  customComparisonRules: any[]
): string[] => {
  if (!customComparisonRules || customComparisonRules.length === 0) {
    return [];
  }

  return customComparisonRules
    .filter(
      (rule: any) =>
        rule?.resource &&
        (isOperandColumnMissing(rule.resource.left_operand) ||
          isOperandColumnMissing(rule.resource.right_operand))
    )
    .map((rule: any) => rule?.name ?? "");
};

/**
 * Builds the selectable column options for a comparison operand's column select.
 *
 * columnsData is derived by removing merge/unique-key columns and then dropping
 * resources left with no columns, so a previously persisted column_name can be
 * missing from its resource's list — or the resource can be absent entirely.
 * A controlled <select> whose value is not among its options renders blank, which
 * makes a correctly persisted binding look lost after reload. The persisted
 * column is therefore always re-included.
 *
 * @param columnsData - Derived per-resource column lists ({ id, columns, code })
 * @param resourceId - Resource id of the operand (may be a string or a number)
 * @param currentColumnName - Currently bound column name, if any
 * @returns Column names to render as options, persisted value included
 */
export const getColumnOptionsForOperand = (
  columnsData: any[],
  resourceId: any,
  currentColumnName?: string | null
): string[] => {
  const resource = columnsData?.find(
    (res: any) => String(res?.id) === String(resourceId)
  );

  const columns: string[] = (resource?.columns || []).filter(
    (col: string) => col && col !== 'file_name'
  );

  // Re-include a persisted column that was filtered out or whose resource is gone
  if (currentColumnName && !columns.includes(currentColumnName)) {
    return [currentColumnName, ...columns];
  }

  return columns;
};

/**
 * Interface for scroll position tracking
 */
export interface ScrollPosition {
  ruleId: number | null;
  offset: number;
}

/**
 * Saves the current scroll position relative to a specific rule element
 * 
 * @param containerId - ID of the scrollable container
 * @param ruleId - ID of the rule currently being edited
 * @returns ScrollPosition object
 */
export const saveScrollPosition = (
  containerId: string,
  ruleId: number | null
): ScrollPosition => {
  const container = document.getElementById(containerId);
  if (!container) {
    return { ruleId, offset: 0 };
  }

  const scrollTop = container.scrollTop;
  
  // Try to find the element for the given ruleId
  if (ruleId !== null) {
    const ruleElement = container.querySelector(`[data-rule-id="${ruleId}"]`);
    if (ruleElement) {
      const elementTop = (ruleElement as HTMLElement).offsetTop;
      return { ruleId, offset: scrollTop - elementTop };
    }
  }

  return { ruleId, offset: scrollTop };
};

/**
 * Restores scroll position to a previously saved position
 * 
 * @param containerId - ID of the scrollable container
 * @param scrollPosition - Previously saved scroll position
 */
export const restoreScrollPosition = (
  containerId: string,
  scrollPosition: ScrollPosition
): void => {
  const container = document.getElementById(containerId);
  if (!container) {
    return;
  }

  // Use requestAnimationFrame to ensure DOM has updated
  requestAnimationFrame(() => {
    if (scrollPosition.ruleId !== null) {
      const ruleElement = container.querySelector(
        `[data-rule-id="${scrollPosition.ruleId}"]`
      );
      if (ruleElement) {
        const elementTop = (ruleElement as HTMLElement).offsetTop;
        container.scrollTop = elementTop + scrollPosition.offset;
        return;
      }
    }

    // Fallback: restore absolute scroll position
    container.scrollTop = scrollPosition.offset;
  });
};
